'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * Subscribe to a media-query match state. SSR-safe by default (returns `false` on the server and
 * through hydration) and uses `useSyncExternalStore` so the render result is consistent across
 * React's concurrent passes.
 *
 * Prefer this over ahooks' `useResponsive` whenever the check isn't "is the viewport within this
 * single named band" — `useResponsive`'s xs/sm/md/lg/xl booleans are each true only inside their
 * own band, not "this band and up", so reusing e.g. `md` for a `min-width` check silently
 * regresses on wider screens.
 *
 * @param query - CSS media query string (e.g., `(min-width: 768px)`).
 * @param initializeWithValue - Read the real `matchMedia` value on the client's first render
 * instead of the SSR-safe `false`, trading the default's brief "reports false until hydration
 * settles" window for hydration-mismatch risk. Safe only for consumers that never hydrate
 * against server-rendered markup for this component — Pages Router hydrates just as much as App
 * Router, so this isn't an App-Router-only caveat. A text-only mismatch settles in the same
 * commit with no visible flash, but a mismatch that changes element structure makes React
 * discard the whole subtree and fall back to full client rendering, which is worse than the
 * default's flash.
 */
export function useMediaQuery(query: string, initializeWithValue = false): boolean {
  const mediaQueryList = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.matchMedia(query);
  }, [query]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!mediaQueryList) return () => {};
      const legacyMediaQueryList = mediaQueryList as MediaQueryList & {
        addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
        removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      };
      const listener = () => onStoreChange();
      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', listener);
        return () => mediaQueryList.removeEventListener('change', listener);
      }
      legacyMediaQueryList.addListener?.(listener);
      return () => legacyMediaQueryList.removeListener?.(listener);
    },
    [mediaQueryList],
  );

  const getSnapshot = useCallback(() => mediaQueryList?.matches ?? false, [mediaQueryList]);
  const getServerSnapshot = initializeWithValue ? getSnapshot : () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
